import { useMemo, useState } from 'react';
import { getInkStyle, type InkColor } from '@/lib/ink-utils';

interface CardImageProps {
  src: string | null;
  alt: string;
  inkColor?: InkColor;
  enchanted?: boolean;
  showGlow?: boolean;
  className?: string;
  /** Optional sizes hint passed to the browser for srcset selection. */
  sizes?: string;
  /** Set to "high" for above-the-fold images. Default lazy. */
  priority?: boolean;
}

/**
 * Derive a responsive srcset for known card-image hosts so phones don't
 * download the full-resolution print.
 *
 * Lorcast: https://cards.lorcast.io/card/digital/large/{id}.avif
 *   → small / normal / large variants are all served by the CDN.
 */
function buildSrcSet(src: string): { srcSet?: string; sizes?: string } {
  if (!src) return {};
  // Lorcast — swap the /large/ path segment for the other sizes.
  const lorcast = src.match(/^(https:\/\/cards\.lorcast\.io\/card\/digital\/)(small|normal|large)(\/.+)$/);
  if (lorcast) {
    const [, base, , rest] = lorcast;
    return {
      srcSet: [
        `${base}small${rest} 220w`,
        `${base}normal${rest} 480w`,
        `${base}large${rest} 1024w`,
      ].join(', '),
    };
  }
  return {};
}

export function CardImage({
  src,
  alt,
  inkColor,
  enchanted,
  showGlow,
  className = '',
  sizes,
  priority = false,
}: CardImageProps) {
  const [loaded, setLoaded] = useState(false);
  const glowClass = showGlow && inkColor ? getInkStyle(inkColor).glow : '';
  const { srcSet } = useMemo(() => (src ? buildSrcSet(src) : {}), [src]);

  return (
    <div className={`relative overflow-hidden rounded-lg ${glowClass} ${className}`}>
      {!loaded && <div className="skeleton-shimmer absolute inset-0" />}
      {src ? (
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes ?? '(min-width: 768px) 18vw, 30vw'}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={() => setLoaded(true)}
          className={`w-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : (
        <div className="flex aspect-[2/3] items-center justify-center bg-secondary text-4xl">🃏</div>
      )}
      {enchanted && (
        <div className="enchanted-sparkle absolute inset-0 pointer-events-none" />
      )}
    </div>
  );
}
